'use client';

import React, { useState, useMemo } from 'react';
import { X, Battery, HardDrive, Palette, Tag, Eye, ChevronDown, Filter, Smartphone, CheckCircle2, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';

// Backend integration point: replace with API call to /api/refurbished-phones?status=listed
const PHONES = [
  {
    id: 'phone-001',
    brand: 'Apple',
    model: 'iPhone 13',
    storage: '128GB',
    colour: 'Black',
    batteryHealth: 100,
    condition: 'Excellent',
    price: 350,
    status: 'Sold',
    description: 'Excellent condition iPhone 13 128GB in Black. Screen is flawless, body shows minimal signs of use. IMEI verified clean.',
    imeiVerified: true,
  },
  {
    id: 'phone-002',
    brand: 'Apple',
    model: 'iPhone 13',
    storage: '128GB',
    colour: 'Starlight',
    batteryHealth: 100,
    condition: 'Excellent',
    price: 350,
    status: 'Sold',
    description: 'Excellent condition iPhone 13 128GB in Starlight. Clean screen, light wear on edges only. Fully functional.',
    imeiVerified: true,
  },
  {
    id: 'phone-003',
    brand: 'Apple',
    model: 'iPhone 11',
    storage: '128GB',
    colour: 'Red',
    batteryHealth: 85,
    condition: 'Good',
    price: 220,
    status: 'Sold',
    description: 'Good condition iPhone 11 128GB in Product Red. Minor scratches on frame, screen is clean. All features working perfectly.',
    imeiVerified: true,
  },
  {
    id: 'phone-004',
    brand: 'Apple',
    model: 'iPhone 11',
    storage: '128GB',
    colour: 'Black',
    batteryHealth: 83,
    condition: 'Good',
    price: 220,
    status: 'Sold',
    description: 'Good condition iPhone 11 128GB in Black. Light wear visible, screen scratch-free. Fully functional.',
    imeiVerified: true,
  },
  {
    id: 'phone-005',
    brand: 'Apple',
    model: 'iPhone 11',
    storage: '128GB',
    colour: 'White',
    batteryHealth: 87,
    condition: 'Good',
    price: 220,
    status: 'Sold',
    description: 'Good condition iPhone 11 128GB in White. Clean screen, minor frame wear. All cameras and Face ID working perfectly.',
    imeiVerified: true,
  },
  {
    id: 'phone-006',
    brand: 'Apple',
    model: 'iPhone 12 Pro Max',
    storage: '256GB',
    colour: 'Silver',
    batteryHealth: 86,
    condition: 'Good',
    price: 380,
    status: 'Sold',
    description: 'Good condition iPhone 12 Pro Max 256GB in Silver. Minor signs of use, screen is clean. Triple camera system fully functional.',
    imeiVerified: true,
  },
];

interface EnquiryFormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

type PhoneType = typeof PHONES[number];
type ConditionType = 'Excellent' | 'Good' | 'Fair';

function conditionClass(condition: string): string {
  if (condition === 'Excellent') return 'badge-excellent';
  if (condition === 'Good') return 'badge-good';
  return 'badge-fair';
}

function batteryClass(health: number): string {
  if (health >= 90) return 'battery-high';
  if (health >= 80) return 'battery-mid';
  return 'battery-low';
}

function PhoneDetailModal({ phone, onClose }: { phone: PhoneType; onClose: () => void }) {
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EnquiryFormData>();

  const onSubmit = async (_data: EnquiryFormData) => {
    setSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1400));
    setSubmitting(false);
    setEnquirySubmitted(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop animate-fade-in"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in"
        style={{ borderRadius: 'var(--bubbly-radius)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.8)', backgroundColor: '#111111', border: '1px solid #2a2a2a' }}
      >
        {/* Header */}
        <div
          className="sticky top-0 px-6 py-4 flex items-center justify-between z-10"
          style={{ backgroundColor: '#111111', borderBottom: '1px solid #2a2a2a', borderTopLeftRadius: 'var(--bubbly-radius)', borderTopRightRadius: 'var(--bubbly-radius)' }}
        >
          <div>
            <h2 className="font-extrabold text-lg" style={{ color: '#f5f0e8' }}>{phone.model}</h2>
            <p className="text-sm" style={{ color: '#8a7a65' }}>{phone.brand} &middot; {phone.storage} &middot; {phone.colour}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-colors"
            style={{ color: '#f5f0e8' }}
            onMouseEnter={e => ((e.currentTarget as HTMLElement).style.backgroundColor = '#1a1a1a')}
            onMouseLeave={e => ((e.currentTarget as HTMLElement).style.backgroundColor = 'transparent')}
            aria-label="Close detail view"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Price + status */}
          <div className="flex items-center justify-between">
            <span className="text-3xl font-extrabold price-tag" style={{ color: '#d4a843' }}>${phone.price}</span>
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${phone.status === 'Listed' ? 'badge-listed' : 'badge-sold'}`}>
              {phone.status === 'Listed' ? 'Available' : 'Sold'}
            </span>
          </div>

          {/* Specs grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { id: 'spec-storage', icon: HardDrive, label: 'Storage', value: phone.storage },
              { id: 'spec-colour', icon: Palette, label: 'Colour', value: phone.colour },
              { id: 'spec-battery', icon: Battery, label: 'Battery Health', value: `${phone.batteryHealth}%`, valueClass: batteryClass(phone.batteryHealth) },
              { id: 'spec-condition', icon: Tag, label: 'Condition', value: phone.condition },
            ].map((spec) => {
              const SpecIcon = spec.icon;
              return (
                <div key={spec.id} className="rounded-2xl p-4 flex items-start gap-3" style={{ backgroundColor: '#1a1a1a' }}>
                  <SpecIcon size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#d4a843' }} />
                  <div>
                    <div className="text-xs font-medium mb-0.5" style={{ color: '#5a4a35' }}>{spec.label}</div>
                    <div className={`text-sm font-bold ${spec.valueClass || ''}`} style={!spec.valueClass ? { color: '#f5f0e8' } : {}}>{spec.value}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* IMEI verified */}
          {phone.imeiVerified && (
            <div className="flex items-center gap-2 text-sm rounded-2xl px-4 py-3" style={{ color: '#4ade80', backgroundColor: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.15)' }}>
              <CheckCircle2 size={15} className="flex-shrink-0" />
              IMEI verified — not reported lost or stolen
            </div>
          )}

          {/* Description */}
          <div>
            <h4 className="text-sm font-bold mb-2" style={{ color: '#f5f0e8' }}>Condition Notes</h4>
            <p className="text-sm leading-relaxed" style={{ color: '#8a7a65' }}>{phone.description}</p>
          </div>

          {/* Enquiry form or sold state */}
          {phone.status === 'Sold' ? (
            <div className="text-center py-6 rounded-2xl" style={{ backgroundColor: '#1a1a1a' }}>
              <p className="text-sm font-semibold" style={{ color: '#8a7a65' }}>This device has been sold.</p>
              <p className="text-xs mt-1" style={{ color: '#5a4a35' }}>Check back soon for new stock, or browse other available phones.</p>
            </div>
          ) : !enquirySubmitted ? (
            <div>
              <h4 className="text-sm font-bold mb-4" style={{ color: '#f5f0e8' }}>Enquire About This Phone</h4>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label" htmlFor="enquiry-name">Your Name</label>
                    <input
                      id="enquiry-name"
                      type="text"
                      className="form-input"
                      placeholder="Alex Chen"
                      {...register('name', { required: 'Name is required' })}
                    />
                    {errors.name && <p className="form-error">{errors.name.message}</p>}
                  </div>
                  <div>
                    <label className="form-label" htmlFor="enquiry-phone">Phone Number</label>
                    <input
                      id="enquiry-phone"
                      type="tel"
                      className="form-input"
                      placeholder="04XX XXX XXX"
                      {...register('phone', { required: 'Phone is required' })}
                    />
                    {errors.phone && <p className="form-error">{errors.phone.message}</p>}
                  </div>
                </div>
                <div>
                  <label className="form-label" htmlFor="enquiry-email">Email Address</label>
                  <input
                    id="enquiry-email"
                    type="email"
                    className="form-input"
                    placeholder="alex@example.com.au"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                    })}
                  />
                  {errors.email && <p className="form-error">{errors.email.message}</p>}
                </div>
                <div>
                  <label className="form-label" htmlFor="enquiry-message">Message <span style={{ color: '#5a4a35', fontWeight: 400 }}>(optional)</span></label>
                  <textarea
                    id="enquiry-message"
                    rows={2}
                    className="form-input resize-none"
                    placeholder="Any questions about this device? Ask us here..."
                    {...register('message')}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 rounded-full h-12 font-bold text-sm transition-all hover:scale-[1.02] disabled:opacity-50"
                  style={{ backgroundColor: '#d4a843', color: '#0a0a0a', boxShadow: '0 4px 16px 0 rgba(212,168,67,0.3)' }}
                >
                  {submitting ? (
                    <><Loader2 size={16} className="animate-spin" /> Sending Enquiry...</>
                  ) : (
                    'Send Enquiry'
                  )}
                </button>
              </form>
            </div>
          ) : (
            <div className="text-center py-6 rounded-2xl" style={{ backgroundColor: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.15)' }}>
              <CheckCircle2 size={28} className="mx-auto mb-3" style={{ color: '#4ade80' }} />
              <h4 className="font-bold mb-1" style={{ color: '#f5f0e8' }}>Enquiry Sent!</h4>
              <p className="text-sm" style={{ color: '#8a7a65' }}>We will be in touch within 1 business day.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RefurbishedListing() {
  const [filterBrand, setFilterBrand] = useState('All');
  const [filterStorage, setFilterStorage] = useState('All');
  const [filterCondition, setFilterCondition] = useState('All');
  const [filterMaxPrice, setFilterMaxPrice] = useState('All');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState<PhoneType | null>(null);

  const listedPhones = PHONES.filter((p) => p.status === 'Listed');
  const soldPhones = PHONES.filter((p) => p.status === 'Sold');

  const allBrands = ['All', ...Array.from(new Set(PHONES.map((p) => p.brand)))];
  const allStorage = ['All', ...Array.from(new Set(PHONES.map((p) => p.storage))).sort()];
  const allConditions: ('All' | ConditionType)[] = ['All', 'Excellent', 'Good', 'Fair'];
  const priceRanges = ['All', 'Under $400', '$400\u2013$700', '$700\u2013$900', 'Over $900'];

  const filteredPhones = useMemo(() => {
    return listedPhones.filter((phone) => {
      if (filterBrand !== 'All' && phone.brand !== filterBrand) return false;
      if (filterStorage !== 'All' && phone.storage !== filterStorage) return false;
      if (filterCondition !== 'All' && phone.condition !== filterCondition) return false;
      if (filterMaxPrice === 'Under $400' && phone.price >= 400) return false;
      if (filterMaxPrice === '$400\u2013$700' && (phone.price < 400 || phone.price > 700)) return false;
      if (filterMaxPrice === '$700\u2013$900' && (phone.price < 700 || phone.price > 900)) return false;
      if (filterMaxPrice === 'Over $900' && phone.price <= 900) return false;
      return true;
    });
  }, [listedPhones, filterBrand, filterStorage, filterCondition, filterMaxPrice]);

  const activeFilterCount = [filterBrand, filterStorage, filterCondition, filterMaxPrice].filter((f) => f !== 'All').length;

  const clearFilters = () => {
    setFilterBrand('All');
    setFilterStorage('All');
    setFilterCondition('All');
    setFilterMaxPrice('All');
  };

  return (
    <>
      <section className="px-6 py-12 lg:px-10 lg:py-16" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="max-w-[1280px] mx-auto">

          {/* Filter bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-extrabold" style={{ color: '#f5f0e8' }}>
                  Available Stock
                  <span className="ml-2 text-sm font-normal" style={{ color: '#5a4a35' }}>({filteredPhones.length} phones)</span>
                </h2>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 text-xs font-semibold transition-colors"
                    style={{ color: '#d4a843' }}
                  >
                    <X size={13} />
                    Clear filters ({activeFilterCount})
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-150"
                style={{
                  border: showFilters || activeFilterCount > 0 ? '1px solid #d4a843' : '1px solid #2a2a2a',
                  backgroundColor: showFilters || activeFilterCount > 0 ? 'rgba(212,168,67,0.08)' : '#1a1a1a',
                  color: showFilters || activeFilterCount > 0 ? '#d4a843' : '#f5f0e8',
                }}
              >
                <Filter size={15} />
                Filters
                {activeFilterCount > 0 && (
                  <span
                    className="w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold"
                    style={{ backgroundColor: '#d4a843', color: '#0a0a0a' }}
                  >
                    {activeFilterCount}
                  </span>
                )}
                <ChevronDown size={14} className={`transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Filter panel */}
            {showFilters && (
              <div
                className="p-5 animate-slide-up"
                style={{ borderRadius: 'var(--bubbly-radius)', boxShadow: 'var(--card-shadow)', backgroundColor: '#111111', border: '1px solid #2a2a2a' }}
              >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <label className="form-label text-xs">Brand</label>
                    <select value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)} className="form-select text-sm py-2.5">
                      {allBrands.map((b) => <option key={`filter-brand-${b}`} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label text-xs">Storage</label>
                    <select value={filterStorage} onChange={(e) => setFilterStorage(e.target.value)} className="form-select text-sm py-2.5">
                      {allStorage.map((s) => <option key={`filter-storage-${s}`} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label text-xs">Condition</label>
                    <select value={filterCondition} onChange={(e) => setFilterCondition(e.target.value)} className="form-select text-sm py-2.5">
                      {allConditions.map((c) => <option key={`filter-cond-${c}`} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label text-xs">Price Range</label>
                    <select value={filterMaxPrice} onChange={(e) => setFilterMaxPrice(e.target.value)} className="form-select text-sm py-2.5">
                      {priceRanges.map((r) => <option key={`filter-price-${r}`} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Phone grid */}
          {filteredPhones.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-16">
              {filteredPhones.map((phone) => (
                <div
                  key={phone.id}
                  className="overflow-hidden hover:shadow-xl transition-all duration-200 cursor-pointer group"
                  style={{ borderRadius: 'var(--bubbly-radius)', boxShadow: 'var(--card-shadow)', backgroundColor: '#111111', border: '1px solid #2a2a2a' }}
                  onClick={() => setSelectedPhone(phone)}
                >
                  {/* Phone visual placeholder */}
                  <div
                    className="h-44 flex items-center justify-center relative"
                    style={{ background: 'linear-gradient(135deg, #1a1500 0%, #221c00 100%)', borderBottom: '1px solid #2a2a2a' }}
                  >
                    <Smartphone size={56} className="transition-colors duration-200" style={{ color: '#3a2e00' }} />
                    <span className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold ${conditionClass(phone.condition)}`}>
                      {phone.condition}
                    </span>
                  </div>

                  <div className="p-5">
                    <div className="mb-3">
                      <p className="text-xs font-medium mb-0.5" style={{ color: '#5a4a35' }}>{phone.brand}</p>
                      <h3 className="font-extrabold text-base leading-snug transition-colors" style={{ color: '#f5f0e8' }}>{phone.model}</h3>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: '#1a1a1a', color: '#8a7a65' }}>
                        <HardDrive size={11} />
                        {phone.storage}
                      </span>
                      <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: '#1a1a1a', color: '#8a7a65' }}>
                        <Palette size={11} />
                        {phone.colour}
                      </span>
                      <span className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-bold ${batteryClass(phone.batteryHealth)}`} style={{ backgroundColor: '#1a1a1a' }}>
                        <Battery size={11} />
                        {phone.batteryHealth}%
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xl font-extrabold price-tag" style={{ color: '#d4a843' }}>${phone.price}</span>
                      <button
                        className="flex items-center gap-1.5 text-xs font-bold transition-colors hover:opacity-80"
                        style={{ color: '#d4a843' }}
                        onClick={(e) => { e.stopPropagation(); setSelectedPhone(phone); }}
                      >
                        <Eye size={14} />
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ backgroundColor: 'rgba(212,168,67,0.1)' }}
              >
                <Smartphone size={24} style={{ color: '#d4a843' }} />
              </div>
              <h3 className="text-lg font-extrabold mb-2" style={{ color: '#f5f0e8' }}>No phones match your filters</h3>
              <p className="text-sm mb-5" style={{ color: '#8a7a65' }}>Try adjusting your filters to see more available stock.</p>
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-colors"
                style={{ border: '1px solid #2a2a2a', backgroundColor: '#1a1a1a', color: '#f5f0e8' }}
              >
                <X size={15} />
                Clear All Filters
              </button>
            </div>
          )}

          {/* Recently sold section */}
          {soldPhones.length > 0 && (
            <div className="pt-12" style={{ borderTop: '1px solid #2a2a2a' }}>
              <h3 className="text-base font-extrabold mb-2" style={{ color: '#f5f0e8' }}>Recently Sold</h3>
              <p className="text-sm mb-6" style={{ color: '#8a7a65' }}>These devices have been sold. Similar stock may become available — check back soon.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {soldPhones.map((phone) => (
                  <div
                    key={phone.id}
                    className="overflow-hidden opacity-50 relative"
                    style={{ borderRadius: 'var(--bubbly-radius)', boxShadow: 'var(--card-shadow)', backgroundColor: '#111111', border: '1px solid #2a2a2a' }}
                  >
                    <div
                      className="h-32 flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #111111 100%)', borderBottom: '1px solid #2a2a2a' }}
                    >
                      <Smartphone size={36} style={{ color: '#2a2a2a' }} />
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className="badge-sold px-2.5 py-1 rounded-full text-xs font-bold">Sold</span>
                    </div>
                    <div className="p-4">
                      <p className="text-xs mb-0.5" style={{ color: '#5a4a35' }}>{phone.brand}</p>
                      <h4 className="font-extrabold text-sm mb-1" style={{ color: '#f5f0e8' }}>{phone.model}</h4>
                      <p className="text-xs" style={{ color: '#5a4a35' }}>{phone.storage} &middot; {phone.colour}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Detail modal */}
      {selectedPhone && (
        <PhoneDetailModal
          phone={selectedPhone}
          onClose={() => setSelectedPhone(null)}
        />
      )}
    </>
  );
}